import { Link, Stack } from '@mui/material';
import { type ReactNode } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { CopyToClipboardButton } from 'common';

import { PatientLink } from './PatientLink';

interface EntityLinkProps {
  entityType: string;
  entityId: string;
  /**
   * Optional click observer. Fires only on the router-link variants
   * (`encounter`, `patientOrder`, `externalDocumentPacket`) after
   * `stopPropagation`; navigation still proceeds. NOT invoked for the
   * `ehrInformation` (PatientLink) or plain-text fallback variant — wire it into
   * those branches too if a future caller needs analytics there. Useful for
   * analytics (PostHog).
   */
  onClick?: () => void;
  /**
   * Overrides the visible link text on the router-link variants (`encounter`,
   * `patientOrder`, `externalDocumentPacket`). Defaults to the entity-named label
   * (e.g. `Encounter <id>`). Use to render just the id when the surrounding
   * context already names the entity.
   */
  label?: ReactNode;
}
export function EntityLink(props: EntityLinkProps) {
  const { entityType, entityId, onClick, label } = props;
  if (entityType === 'ehrInformation') {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <PatientLink ehrInformationId={entityId} />
        <CopyToClipboardButton animationVariant="AlwaysVisible" content={entityId} />
      </Stack>
    );
  }
  if (entityType === 'encounter') {
    return (
      <Link
        component={ReactRouterLink}
        style={{ color: 'inherit' }}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        to={`/entity/encounters/${entityId}`}
      >
        {label ?? `Encounter ${entityId}`}
      </Link>
    );
  }
  if (entityType === 'patientOrder') {
    return (
      <Link
        component={ReactRouterLink}
        style={{ color: 'inherit' }}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        to={`/entity/orders/${entityId}`}
      >
        {label ?? `Order ${entityId}`}
      </Link>
    );
  }
  if (entityType === 'externalDocumentPacket') {
    return (
      <Link
        component={ReactRouterLink}
        style={{ color: 'inherit' }}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        to={`/entity/externalDocumentPackets/${entityId}`}
      >
        {label ?? `Doc Packet ${entityId}`}
      </Link>
    );
  }
  return (
    <span>
      {entityType}: {entityId}
    </span>
  );
}
